import { type ComponentProps, useEffect, useRef } from 'react';
import { Controller, useFormContext, type FieldPath } from 'react-hook-form';
import type { CountryCode } from 'libphonenumber-js';

import { TextField } from '@/components/form-elements/Field';
import PhoneField from '@/components/form-elements/PhoneField';
import RelationshipField from '@/components/form-elements/RelationshipField';
import { RELATIONSHIP_OPTIONS } from '@/constants';
import { normaliseNameInput } from '@/utils/formatters/string';
import {
  DEFAULT_PHONE_COUNTRY,
  detectCountryFromInput,
  isPhoneCountry,
} from '@/utils/helpers/phone.helpers';
import {
  type FormValues,
  MAX_EMAIL_LENGTH,
  MAX_PHONE_LENGTH,
  NAME_MAX_LENGTH,
} from '@/components/forms/GiftDetails/schema';

type GiftDetailsFormProps = {
  onSubmit: NonNullable<ComponentProps<'form'>['onSubmit']>;
  /** Child's first name for "Relationship to Ava" / "Name Ava knows you by". */
  childFirstName?: string;
  /**
   * Set once Continue has been pressed with empty required fields: every
   * required field that is still empty gets the visual nudge (GIFTWEB-16 AC 4/5).
   */
  showRequired?: boolean;
};

type FieldName = FieldPath<FormValues>;
const REQUIRED_FIELDS: readonly FieldName[] = [
  'legalFirstName',
  'legalLastName',
  'email',
  'relationshipToChild',
];

function GiftDetailsForm({
  onSubmit,
  childFirstName,
  showRequired = false,
}: GiftDetailsFormProps) {
  const {
    register,
    control,
    trigger,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<FormValues>();

  const values = watch();
  const child = childFirstName?.trim() || '';
  const relationshipLabel = child ? `Relationship to ${child}` : 'Relationship to the child';
  const knownAsLabel = child ? `Name ${child} knows you by` : 'Name the child knows you by';

  const phoneInputRef = useRef<HTMLInputElement | null>(null);
  const emailInputRef = useRef<HTMLInputElement | null>(null);

  // iOS autofill fires a native 'change' without React's synthetic onChange —
  // re-validate so the inline state catches up.
  useEffect(() => {
    const phoneEl = phoneInputRef.current;
    const emailEl = emailInputRef.current;
    const handlePhoneChange = () => trigger('phone');
    const handleEmailChange = () => trigger('email');
    phoneEl?.addEventListener('change', handlePhoneChange);
    emailEl?.addEventListener('change', handleEmailChange);
    return () => {
      phoneEl?.removeEventListener('change', handlePhoneChange);
      emailEl?.removeEventListener('change', handleEmailChange);
    };
  }, [trigger]);

  const isEmpty = (name: FieldName) => !String(values[name] ?? '').trim();
  const nudge = (name: FieldName) =>
    showRequired && REQUIRED_FIELDS.includes(name) && isEmpty(name);
  // Empty required fields get the (text-free) nudge, not an error message;
  // an error message is for a value that is present but invalid.
  const errorFor = (name: FieldName) =>
    !isEmpty(name) ? errors[name]?.message : undefined;

  // Names: normalise smart quotes / unicode dashes as the user types, then
  // hand the event to RHF so validation mode is respected.
  const nameField = (name: 'legalFirstName' | 'legalLastName' | 'nameKnownAs') => {
    const reg = register(name);
    return {
      ...reg,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        e.target.value = normaliseNameInput(e.target.value);
        return reg.onChange(e);
      },
    };
  };

  // Register in DOM order: RHF focuses the first *registered* field in error
  // after an invalid submit, which must be the first field on screen (AC 4).
  const firstNameField = nameField('legalFirstName');
  const lastNameField = nameField('legalLastName');
  const { ref: emailRef, ...emailRest } = register('email');
  const { ref: phoneRef, ...phoneReg } = register('phone');
  const knownAsField = nameField('nameKnownAs');

  // ── Worldwide mobile number (GIFTWEB-17) ─────────────────────────────────
  const phoneCountryReg = register('phoneCountry');
  const rawPhoneCountry = values.phoneCountry ?? '';
  const phoneCountry: CountryCode = isPhoneCountry(rawPhoneCountry)
    ? rawPhoneCountry
    : DEFAULT_PHONE_COUNTRY;

  const handleCountryChange = (country: CountryCode) => {
    setValue('phoneCountry', country, { shouldDirty: true });
    // AC 7: changing the country re-validates any number already entered.
    if (getValues('phone').trim()) void trigger('phone');
  };

  const phoneRest = {
    ...phoneReg,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      // A number typed with its own "+CC" moves the chip to that country so
      // the dial code shown always matches what will be stored.
      const detected = detectCountryFromInput(e.target.value);
      if (detected && detected !== phoneCountry) {
        setValue('phoneCountry', detected, { shouldDirty: true });
      }
      return phoneReg.onChange(e);
    },
  };

  return (
    <form
      id='gift-details-form'
      onSubmit={onSubmit}
      noValidate
      className='grid grid-cols-1 gap-3.5 lg:grid-cols-2 lg:gap-x-4.5 lg:gap-y-[15px]'
    >
      <TextField
        id='legalFirstName'
        label='Legal first name'
        placeholder='First name'
        autoComplete='given-name'
        maxLength={NAME_MAX_LENGTH}
        required
        error={errorFor('legalFirstName')}
        nudge={nudge('legalFirstName')}
        {...firstNameField}
      />
      <TextField
        id='legalLastName'
        label='Legal last name'
        placeholder='Last name'
        autoComplete='family-name'
        maxLength={NAME_MAX_LENGTH}
        required
        error={errorFor('legalLastName')}
        nudge={nudge('legalLastName')}
        {...lastNameField}
      />
      <TextField
        id='email'
        className='lg:col-span-2'
        label='Email address'
        type='email'
        inputMode='email'
        autoComplete='email'
        placeholder='you@example.com'
        maxLength={MAX_EMAIL_LENGTH}
        required
        hint='Already have a Mia account? Please use the same email address.'
        error={errorFor('email')}
        nudge={nudge('email')}
        {...emailRest}
        ref={(el) => {
          emailRef(el);
          emailInputRef.current = el;
        }}
      />
      <PhoneField
        id='phone'
        className='lg:col-span-2'
        label='Mobile number'
        hint='UK and international numbers welcome'
        country={phoneCountry}
        onCountryChange={handleCountryChange}
        countrySelectProps={{
          name: phoneCountryReg.name,
          onBlur: phoneCountryReg.onBlur,
          ref: phoneCountryReg.ref,
        }}
        maxLength={MAX_PHONE_LENGTH}
        error={errorFor('phone')}
        {...phoneRest}
        ref={(el) => {
          phoneRef(el);
          phoneInputRef.current = el;
        }}
      />
      <Controller
        name='relationshipToChild'
        control={control}
        render={({ field }) => (
          <RelationshipField
            id='relationshipToChild'
            name={field.name}
            ref={field.ref}
            label={relationshipLabel}
            sheetTitle={relationshipLabel}
            placeholder='Select relationship'
            options={RELATIONSHIP_OPTIONS}
            required
            value={field.value ?? ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
            error={errorFor('relationshipToChild')}
            nudge={nudge('relationshipToChild')}
          />
        )}
      />
      <TextField
        id='nameKnownAs'
        label={knownAsLabel}
        placeholder='e.g. Grandad, Auntie Em'
        autoComplete='nickname'
        maxLength={NAME_MAX_LENGTH}
        error={errorFor('nameKnownAs')}
        {...knownAsField}
      />
    </form>
  );
}

export default GiftDetailsForm;
